/*
 * The rules behind the ImageViewer that need no browser: how an image fits the stage, how far it
 * zooms and pans, and what a key or the wheel asks of it.
 *
 * The image stands fitted in the middle of the stage, never past its own size (`fitted`), and is
 * zoomed by `scale` (1 is fitted) and moved off the middle by `x` and `y`, in px. It zooms from 1 to
 * `mostScale`, and pans only as far as keeps it over the stage: an image narrower than the stage
 * stays in the middle across, and one wider stops with its edge at the stage's.
 */

/** A width and height, in px. */
export type Size = { width: number; height: number }

/** How the image stands: zoomed by `scale` from fitted, and `x`, `y` off the stage's middle. */
export type ImageView = { scale: number; x: number; y: number }

/** Fitted, in the middle. */
export const unzoomed: ImageView = { scale: 1, x: 0, y: 0 }

/** The furthest the image zooms in, from fitted. */
export const mostScale = 8

/** How far a button, a key or a double press zooms at a step. */
export const stepFactor = 1.5

/** How far an arrow key pans, in px. */
export const panStep = 48

/** The image's `natural` size, shrunk to fit `stage` whole, never grown past its own. */
export function fitted(natural: Size, stage: Size): Size {
  if (natural.width <= 0 || natural.height <= 0) return { width: 0, height: 0 }
  const shrink = Math.min(1, stage.width / natural.width, stage.height / natural.height)
  return { width: natural.width * shrink, height: natural.height * shrink }
}

/** How far the image may move off the middle along an axis: none while it fits inside. */
const reach = (image: number, stage: number, scale: number) =>
  Math.max(0, (image * scale - stage) / 2)

const within = (value: number, bound: number) => Math.min(bound, Math.max(-bound, value))

/** `view` held to the zoom's bounds, and to a pan that keeps the image `image` over `stage`. */
export function heldView(view: ImageView, image: Size, stage: Size): ImageView {
  const scale = Math.min(mostScale, Math.max(1, view.scale))
  return {
    scale,
    x: within(view.x, reach(image.width, stage.width, scale)),
    y: within(view.y, reach(image.height, stage.height, scale)),
  }
}

/**
 * `view` zoomed by `factor` about `point` (from the stage's middle, in px), so what is under the
 * point stays under it, as a wheel or a pinch zooms where it is.
 */
export function zoomedAt(
  view: ImageView,
  factor: number,
  point: { x: number; y: number },
  image: Size,
  stage: Size,
): ImageView {
  const scale = Math.min(mostScale, Math.max(1, view.scale * factor))
  const ratio = scale / view.scale
  return heldView(
    {
      scale,
      x: point.x - (point.x - view.x) * ratio,
      y: point.y - (point.y - view.y) * ratio,
    },
    image,
    stage,
  )
}

/** `view` moved by `dx`, `dy`, as a drag or an arrow key pans it. */
export function pannedBy(view: ImageView, dx: number, dy: number, image: Size, stage: Size) {
  return heldView({ ...view, x: view.x + dx, y: view.y + dy }, image, stage)
}

/**
 * How far a turn of the wheel zooms: in for a turn away, out toward. A trackpad's pinch comes as
 * the wheel too. `deltaMode` 1 counts lines, 0 pixels.
 */
export function wheelFactor(deltaY: number, deltaMode: number) {
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaY
  return Math.exp(-Math.max(-100, Math.min(100, pixels)) * 0.005)
}

/** What a key pressed in the viewer asks of the image. */
export type ViewerAsked =
  { kind: "zoom"; asked: "in" | "out" | "fit" } | { kind: "pan"; dx: number; dy: number }

/** What `key` asks of the image: +, - and 0 zoom (with Ctrl or ⌘ too), the arrow keys pan. */
export function viewerKeyAsked(
  event: Pick<KeyboardEvent, "key" | "altKey">,
): ViewerAsked | undefined {
  if (event.altKey) return undefined
  if (event.key === "+" || event.key === "=") return { kind: "zoom", asked: "in" }
  if (event.key === "-" || event.key === "_") return { kind: "zoom", asked: "out" }
  if (event.key === "0") return { kind: "zoom", asked: "fit" }
  // An arrow key moves the image the way it points, showing more of that side.
  const pans: Record<string, [number, number]> = {
    ArrowLeft: [panStep, 0],
    ArrowRight: [-panStep, 0],
    ArrowUp: [0, panStep],
    ArrowDown: [0, -panStep],
  }
  const pan = pans[event.key]
  return pan && { kind: "pan", dx: pan[0], dy: pan[1] }
}

/** How large the image shows, as a share of its own size, as the viewer says it. */
export const shownWords = (view: ImageView, image: Size, natural: Size) =>
  natural.width > 0 ? `${Math.round(((image.width * view.scale) / natural.width) * 100)}%` : ""
